import { useEffect, useMemo, useState } from "react";
import "./DesktopLogin.css";
import { authApi, setAuthToken } from "../../../api";
import { useLang } from "../../../i18n/LangContext";

const FALLBACK = {
  eyebrow: "VERIFY EMAIL",
  title: "Verifying your email...",
  successTitle: "Email verified",
  successSubtitle: "Your email has been verified successfully.",
  errorTitle: "Verification failed",
  missingToken: "Missing verification token.",
  genericError: "Unable to verify email. Please try again.",
  button: "Go to dashboard",
};

export default function DesktopVerifyEmail({ onSuccess = () => {} }) {
  const { t } = useLang();
  const copy = useMemo(() => t.verifyEmail || FALLBACK, [t.verifyEmail]);

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState(copy.title || FALLBACK.title);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage(copy.missingToken || FALLBACK.missingToken);
      return;
    }

    let cancelled = false;
    async function run() {
      try {
        const response = await authApi.verifyEmail(token);
        const result = response?.result || response || {};
        const jwt = result?.token || "";
        const user = result?.user || null;
        if (jwt) setAuthToken(jwt);
        if (!cancelled) {
          onSuccess({ token: jwt, user });
          setStatus("success");
          setMessage(copy.successSubtitle || FALLBACK.successSubtitle);
        }
      } catch (err) {
        const msg =
          err?.data?.statusText ||
          err?.data?.statusDesc ||
          err?.message ||
          copy.genericError ||
          FALLBACK.genericError;
        if (!cancelled) {
          setStatus("error");
          setMessage(msg);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSuccess = status === "success";
  const title = isSuccess
    ? copy.successTitle || FALLBACK.successTitle
    : status === "error"
      ? copy.errorTitle || FALLBACK.errorTitle
      : copy.title || FALLBACK.title;

  return (
    <div className="desktop-login-overlay" role="dialog" aria-modal="true">
      <div className="desktop-login-card">
        <p className="desktop-login-eyebrow">{copy.eyebrow}</p>
        <h2 className="desktop-login-title">{title}</h2>
        <p className="desktop-login-subtitle">{message}</p>
        {status === "success" && (
          <button
            type="button"
            className="desktop-login-submit"
            onClick={() => onSuccess({})}
          >
            <span>{(copy.button || FALLBACK.button).toUpperCase()}</span>
          </button>
        )}
      </div>
    </div>
  );
}
